package frontend.pages

import androidx.compose.runtime.*
import com.ionspin.kotlin.bignum.decimal.BigDecimal
import com.ionspin.kotlin.bignum.decimal.toBigDecimal
import components.*
import frontend.App
import frontend.lib.DateTimeDisplay
import frontend.lib.classList
import frontend.models.User
import kotlinx.browser.localStorage
import kotlinx.coroutines.InternalCoroutinesApi
import net.sergeych.sprintf.sprintf
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.events.SyntheticChangeEvent
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.asList
import org.w3c.files.File


@Composable
fun MintingScreen(app: App, user: User) {
    RoundedContainer {
        TButton(attrs = {
            size(ButtonSize.base)
            color(ButtonColor.Green)
            onClick { app.router.dispatch("/minting/draft") }
        }) { Text("Mint UNIT") }
    }
}

@Composable
fun FileIcon() {
    Svg(attrs = {
        classList("mx-auto h-12 w-12 text-gray-400")
        attr("xmlns", "http://www.w3.org/2000/svg")
        attr("fill", "none")
        attr("viewBox", "0 0 48 48")
        attr("stroke", "currentColor")

    }) {
        Path(attrs = {
            attr("stroke-linecap", "round")
            attr("stroke-linejoin", "round")
            attr("stroke-width", "2")
            attr("d", "M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02")
        })
    }
}

data class DraftFile(
    val name: String?,
    val description: String?,
    val file: Uint8Array?,
    val size: Int
    )

@Composable
fun MintingItemScreen(app: App, user: User, id: String) {
    val MaxSize = 10 * 1000 * 1000 // 10 MB (OS X)

    fun k(name: String): String { return "minting-${id}-${name}" }

    fun get(key: String): String? { return localStorage.getItem(k(key)) }
    fun remove(key: String) { localStorage.removeItem(k(key)) }
    fun getBigDecimal(key: String): BigDecimal? { return get(key)?.toBigDecimal() }

    fun set(key: String, value: String?) {
        if (value != null) localStorage.setItem(k(key), value) else remove(key)
    }
    fun setBigDecimal(key: String, num: BigDecimal?) {
        if (num != null) set(key, num.toPlainString()) else remove(key)
    }

    var name: String? by mutableStateOf(get("name"))
    var description: String? by mutableStateOf(get("description"))
    var amount: BigDecimal? by mutableStateOf(getBigDecimal("amount"))
    var attachments = mutableStateListOf<DraftFile>()

    fun onChangeName(value: String?) {
        name = value
        set("name", value)
    }

    fun onChangeAmount(value: BigDecimal?) {
        amount = value
        setBigDecimal("amount", value)
    }

    fun onChangeDescription(value: String?) {
        description = value
        set("description", value)
    }

    fun addFile(file: File?) {
        if (file == null || file.size.toInt() > MaxSize) return

        val draft = DraftFile(file.name, null, null, file.size.toInt())
        attachments.add(draft)
    }

    @Composable
    fun AttachmentRow(
        a: DraftFile,
        onChange: (a: DraftFile) -> Unit,
        onDelete: () -> Unit
    ) {
        val mbs: Double = a.size.toDouble() / (1000 * 1000)

        Tr() {
            Cell { Text(a.name ?: "") }
            Cell { Text("%.2f Mbs".sprintf(mbs)) }
            Cell {
                TTextInput("attachment-description-${a.hashCode()}", a.description ?: "", attrs = {
                    onChange { onChange(a.copy(description = it)) }
                    onInput { onChange(a.copy(description = it)) }
                })
            }
            Cell { TLink("Delete") { onDelete() } }
        }
    }

    RoundedContainer {
        TForm {
            Div(attrs = { classList("grid gap-x-6 grid-cols-2") }) {
                TTextInput("name", name ?: "", attrs = {
                    label { Text("name") }
                    onInput { onChangeName(it) }
                })

                TDecimalInput("amount", amount, attrs = {
                    label { Text("Amount") }
                    onChange { onChangeAmount(it) }
                    onInput { onChangeAmount(it) }
                    precision(2)
                })
            }

            TTextInput("description", description ?: "", attrs = {
                label { Text("Description") }
                onChange { onChangeDescription(it) }
                onInput { onChangeDescription(it) }
            })

            Div(attrs = { classList("mt-1") }) {
                Label(attrs = { classList("block text-sm font-medium text-gray-700") }) {
                    Text("Attachments")
                }

                if (attachments.size > 0) {
                    Table(attrs = { classList("mt-1 min-w-full divide-y divide-gray-200") }) {
                        Thead(attrs = { classList("bg-gray-50") }) {
                            Tr() {
                                HeaderCell { Text("Name") }
                                HeaderCell { Text("Size") }
                                HeaderCell { Text("Description") }
                                HeaderCell { }
                            }
                        }

                        Tbody {
                            for (attachment in attachments) {
                                AttachmentRow(attachment,
                                    onChange = { attachments.set(attachments.indexOf(attachment), it) },
                                    onDelete = { attachments.remove(attachment) }
                                )
                            }
                        }
                    }
                }

                Div(attrs = {
                    classList("mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md")
                    onDrop {
                        it.preventDefault()
                        addFile(it.dataTransfer?.files?.asList()?.firstOrNull())
                    }
                    onDragOver {
                        it.preventDefault()
                    }
                }) {
                    Div(attrs = { classList("space-y-1 text-center") }) {
                        FileIcon()
                        Div(attrs = { classList("flex text-sm text-gray-600") }) {
                            Label("attachment", attrs = {
                                classList("relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500")
                            }) {
                                Span { Text("Upload a file") }
                                Input(InputType.File) {
                                    id("attachment")
                                    classes("sr-only")
                                    onChange {
                                        addFile(it.target.files?.asList()?.firstOrNull())
                                        it.target.value = ""
                                    }
                                }
                            }
                            P(attrs = { classList("pl-1") }) { Text("or drag and drop") }
                        }

                        P(attrs = { classList("text-xs text-gray-500") }) { Text("PDF up to 10MB") }
                    }
                }
            }
        }
    }
}


@Composable
fun MintingUI(app: App, user: User, params: MatchResult? = null) {
    if (params == null) MintingScreen(app, user)
    else {
        val (id) = params.destructured

        MintingItemScreen(app, user, id)
    }
}